import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import ls from 'localstorage-slim';
import { Subject } from 'rxjs';
import { environment } from './../environments/environment';
import {
  AUTHORIZATION,
  CLIENT_NAME,
  PRIVATE_KEY,
  PUBLIC_KEY,
  X_CLIENT_ID,
  X_CLIENT_TYPE
} from './app.constants';
import { AuthService } from './core/auth/services/auth.service';
import { SpinnerService } from './services/spinner/spinner.service';
import { AppState } from './store';
import { reduxReset } from './store/actions/root/root.actions';

ls.config.encrypt = true;

@Component({
  selector: 'lns-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy, AfterContentChecked {
  loading$: any;
  private unsubscribe: Subject<void> = new Subject<void>();
  private boundResetTimer = this.resetTimer.bind(this); // Single bound instance for event listeners
  private time: any;
  private lastActivityTime: number = Date.now();
  private checkInactivityInterval: any;
  autoLogout: boolean | null = null;
  clientType: any = ls.get(X_CLIENT_TYPE);
  private readonly INACTIVITY_TIMEOUT = 300000; // 5 minutes
  private readonly CHECK_INTERVAL = 1000; // Check every second for more precision

  constructor(
    private translate: TranslateService,
    private spinner: SpinnerService,
    private router: Router,
    private ref: ChangeDetectorRef,
    private authService: AuthService,
    private store: Store<AppState>
  ) {
    this.translate.addLangs(['en', 'si']);
    const currentLang = localStorage.getItem('language') || 'si';
    this.translate.setDefaultLang(currentLang);

    if (environment.production) {
      // Add both beforeunload and unload events for better browser compatibility
      window.addEventListener('beforeunload', this.handlePageUnload);
      window.addEventListener('unload', this.handlePageUnload);

      // Also handle page visibility change in production
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
  }

  // ngOnInit(): void {
  //   this.autoLogout = this.authService.getAutoLogout();
  //   if (!this.autoLogout) {
  //     this.autoLogout = true;
  //     this.authService.setAutoLogout(true);
  //   }

  //   if (!environment.production) {
  //     const storedToken: string | null = ls.get(AUTHORIZATION) || null;
  //     if (storedToken) {
  //       this.authService.setToken(storedToken);
  //     }
  //   }

  //   this.setupInactivityListeners();
  //   this.startInactivityCheck();
  //   this.loading$ = this.spinner.loading$;

  //   document.addEventListener('visibilitychange', () => {
  //     if (document.visibilityState === 'visible') {
  //       this.updateLastActivity();
  //     }
  //   });

  //   document.addEventListener('mouseover', event => {
  //     const target = event.target as HTMLElement;
  //     if (target.matches('a, button, .nav-item, .tab, [role="tab"]')) {
  //       this.updateLastActivity();
  //     }
  //   });
  // }

  ngOnInit(): void {
    this.autoLogout = this.authService.getAutoLogout();
    if (!this.autoLogout) {
      this.autoLogout = true;
      this.authService.setAutoLogout(true);
    }

    // Retrieve last activity time from localStorage
    const lastActivity = localStorage.getItem('lastActivityTime');
    if (lastActivity) {
      const inactivityTime = Date.now() - parseInt(lastActivity, 10);
      if (inactivityTime > this.INACTIVITY_TIMEOUT) {
        console.log('User was inactive for more than 5 minutes. Logging out.');
        this.logoutUser();
        return;
      }
    }

    if (!environment.production) {
      const storedToken: string | null = ls.get(AUTHORIZATION) || null;
      if (storedToken) {
        this.authService.setToken(storedToken);
      }
    }

    this.setupInactivityListeners();
    this.startInactivityCheck();
    this.loading$ = this.spinner.loading$;
  }

  private formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  private startInactivityCheck(): void {
    if (this.checkInactivityInterval) {
      clearInterval(this.checkInactivityInterval);
    }

    let timeoutAt = Date.now() + this.INACTIVITY_TIMEOUT;
    console.log(
      'Auto logout will trigger in:',
      this.formatTime(this.INACTIVITY_TIMEOUT)
    );

    this.checkInactivityInterval = setInterval(() => {
      const currentTime = Date.now();

      // If there's been activity, update the timeout time
      if (currentTime - this.lastActivityTime < 1000) {
        timeoutAt = currentTime + this.INACTIVITY_TIMEOUT;
        console.log(
          'Activity detected, reset timer. Will logout in:',
          this.formatTime(this.INACTIVITY_TIMEOUT)
        );
      }

      // Show remaining time
      const remainingTime = timeoutAt - currentTime;
      if (remainingTime > 0 && remainingTime % 5000 === 0) {
        // Log every 5 seconds
        console.log('Time until logout:', this.formatTime(remainingTime));
      }

      // Check if we've reached the timeout
      if (currentTime >= timeoutAt) {
        if (this.autoLogout && this.authService.getToken()) {
          console.log(
            'Auto logout triggered after:',
            this.formatTime(this.INACTIVITY_TIMEOUT)
          );
          this.logoutUser();
        }
      }
    }, this.CHECK_INTERVAL);
  }

  setupInactivityListeners() {
    this.cleanupEventListeners();
    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click'
    ];

    events.forEach(event => {
      document.addEventListener(event, this.boundResetTimer);
    });
  }

  cleanupEventListeners() {
    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click'
    ];

    events.forEach(event => {
      document.removeEventListener(event, this.boundResetTimer);
    });
  }

  private updateLastActivity(): void {
    this.lastActivityTime = Date.now();
    localStorage.setItem('lastActivityTime', this.lastActivityTime.toString());
  }

  resetTimer() {
    this.updateLastActivity();
  }

  logoutUser() {
    this.authService.logout();

    ls.remove(PRIVATE_KEY);
    ls.remove(PUBLIC_KEY);
    ls.remove('client');
    ls.remove(X_CLIENT_ID);
    ls.remove(CLIENT_NAME);
    ls.remove(AUTHORIZATION);
    this.store.dispatch(reduxReset());

    this.router.navigate(['/login']).catch(err => {
      console.error('Error during logout navigation:', err);
    });
  }

  // private handlePageUnload = (event: BeforeUnloadEvent | Event): void => {
  //   if (environment.production && this.authService.isAuthenticated()) {
  //     this.authService.clearToken();
  //     ls.remove(PRIVATE_KEY);
  //     ls.remove('client');
  //     ls.remove(X_CLIENT_ID);
  //     ls.remove(CLIENT_NAME);
  //     ls.remove(PUBLIC_KEY);
  //     ls.remove(AUTHORIZATION);
  //     sessionStorage.clear();
  //     this.store.dispatch(reduxReset());
  //   }
  // };

  private handlePageUnload = (event: BeforeUnloadEvent | Event): void => {
    if (environment.production && this.authService.isAuthenticated()) {
      const lastActivity = localStorage.getItem('lastActivityTime');
      if (lastActivity) {
        const inactivityTime = Date.now() - parseInt(lastActivity, 10);
        if (inactivityTime > this.INACTIVITY_TIMEOUT) {
          console.log('Logging out due to inactivity after tab close.');
          this.logoutUser();
        }
      }
    }
  };

  private handleVisibilityChange = (): void => {
    if (environment.production && document.visibilityState === 'hidden') {
      this.handlePageUnload(new Event('visibilitychange'));
    }
  };

  ngAfterContentChecked() {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    clearTimeout(this.time);
    clearInterval(this.checkInactivityInterval);
    this.cleanupEventListeners();

    // Remove all page unload related listeners
    if (environment.production) {
      window.removeEventListener('beforeunload', this.handlePageUnload);
      window.removeEventListener('unload', this.handlePageUnload);
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
  }
}
